import type { AssetTemplate, AssetTemplates } from '~/modules/shopify';

import { findVariantByType, IVariantType } from '~/modules/campaign';
import { TEMPLATE_TYPE_USED_FOR_FILTER_DEFAULT } from '../constants';
import { useAssetTemplateContext } from '../contexts';
import { checkTemplateDefaultOnGP } from '../helpers';
import { ProviderSource } from '~/types/template';
import { parseTemplateInfoBy } from '~/utils';
import { useCallback, useMemo } from 'react';

const shouldFilter = (template: AssetTemplate): boolean => {
  const info = parseTemplateInfoBy({ templateSuffix: template.key });
  return info ? TEMPLATE_TYPE_USED_FOR_FILTER_DEFAULT.includes(info.type) : false;
};

const isShopifyOriginDefault = (defaults: AssetTemplates, type?: string): boolean => {
  if (!type) return false;

  const match = defaults.find((t) => t.themePageInfo?.templateInfo.type === type);
  const provider = match?.themePageInfo?.defaultByProvider;
  return !provider || provider === ProviderSource.SHOPIFY_ORIGIN;
};

export const useFilterTemplateDefault = () => {
  const { modalAssetTemplate, themePages = [] } = useAssetTemplateContext();
  const { modalData } = modalAssetTemplate;

  const control = useMemo(
    () => findVariantByType(modalData?.campaign, IVariantType.VARIANT_A),
    [modalData?.campaign],
  );

  const templateSuffix = control?.templateSuffix;
  const isControlDefaultOnGP = checkTemplateDefaultOnGP(themePages, templateSuffix);

  const controlProviderSource = useMemo(() => {
    if (!templateSuffix) return;

    const templateInfo = parseTemplateInfoBy({ templateSuffix });
    return templateInfo?.providerSource;
  }, [templateSuffix]);

  const filterDefault = useCallback(
    ({
      templates,
      templateDefaults,
    }: {
      templates: AssetTemplates;
      templateDefaults: AssetTemplates;
    }) => {
      const isShopifyDefault = isShopifyOriginDefault(templateDefaults, control?.templateType);

      return templates.filter((template) => {
        if (!shouldFilter(template)) return true;

        const templateSuffix = template.key;

        const templateInfo = parseTemplateInfoBy({ templateSuffix });
        const provider = templateInfo?.providerSource;

        const isOrigin = provider === ProviderSource.SHOPIFY_ORIGIN;
        const isBackup = provider === ProviderSource.SHOPIFY_GP_BACKUP;

        /**
         * Case 1: A is Shopify origin default:
         * - A is Backup: hide origin
         * - Default: hide backup
         * */
        if (isShopifyDefault) {
          switch (controlProviderSource) {
            case ProviderSource.SHOPIFY_GP_BACKUP:
              return !isOrigin;
          }

          return !isBackup;
        }

        /**
         * Case 2: A is GemPages default:
         * - A is origin: hide base template of GP default
         * - A is backup: hide base template of GP default
         * - A is GP & A is default: hide origin
         * */
        const isTemplateDefaultOnGP = checkTemplateDefaultOnGP(themePages, template.key);
        switch (controlProviderSource) {
          case ProviderSource.SHOPIFY_ORIGIN:
          case ProviderSource.SHOPIFY_GP_BACKUP:
            return !isTemplateDefaultOnGP;

          case ProviderSource.GEMPAGESV7:
            if (isControlDefaultOnGP) return !isOrigin;
            return true;
        }

        return true;
      });
    },
    [control?.templateType, themePages, controlProviderSource, isControlDefaultOnGP],
  );

  return { filterDefault };
};
